/**
 * SerialNumbersPanel.js - Gestão de Números de Série para produtos
 * CRUD completo: Listar, Adicionar, Editar, Remover
 */

import { useState, useEffect, useCallback } from 'react';
import { getErrorMessage, itemsAPI, settingsAPI, serialNumbersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, Edit, Trash2, RefreshCw, Hash, 
  CheckCircle, AlertTriangle, ArrowRightLeft 
} from 'lucide-react';
import { cn, formatDateTime } from '@/lib/utils';
import { useLanguage } from '@/i18n';

export default function SerialNumbersPanel({ productId, productName, onClose }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({ serial_number: '', imei1: '', imei2: '', imei3: '', notes: '', justification: '' });
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonError, setDeleteReasonError] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moveReasonError, setMoveReasonError] = useState('');
  const [moveLocationId, setMoveLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const loadSerialNumbers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100, product_id: productId };
      if (searchTerm) params.search = searchTerm;
      
      const res = await serialNumbersAPI.list(params);
      setSerialNumbers(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error loading serial numbers:', error);
      toast.error('Erro ao carregar números de série');
    } finally {
      setLoading(false);
    }
  }, [productId, searchTerm]);

  const loadLocations = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await settingsAPI.listActiveLocations();
      const rawLocations = response?.data || [];
      const filtered = rawLocations.filter(
        (loc) => loc.is_active !== false && loc.is_hidden !== true && loc.is_deleted !== true
      );
      setLocations(filtered);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  useEffect(() => {
    if (productId) {
      loadSerialNumbers();
    }
  }, [productId, loadSerialNumbers]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const getReasonError = (value) => {
    const trimmed = (value || '').trim();
    if (trimmed.length < 10) {
      return 'Motivo obrigatório (mín. 10 caracteres)';
    }
    return '';
  };

  const getDisplayIdentifier = (item) => item.imei1 || item.serial_number || item.display_sn || 'N/A';

  const handleAdd = async () => {
    if (!formData.serial_number.trim()) {
      toast.error('Número de série é obrigatório');
      return;
    }
    
    try {
      await itemsAPI.addSerialNumber(productId, {
        serial_number: formData.serial_number.trim(),
        notes: formData.notes
      });
      toast.success('Número de série adicionado com sucesso');
      setAddModalOpen(false);
      setFormData({ serial_number: '', imei1: '', imei2: '', imei3: '', notes: '', justification: '' });
      loadSerialNumbers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao adicionar número de série'));
    }
  };

  const handleEdit = async () => {
    if (!formData.serial_number.trim() && !formData.imei1.trim() && !formData.imei2.trim() && !formData.imei3.trim()) {
      toast.error('Preencha pelo menos um identificador: IMEI ou número de série');
      return;
    }

    if (!formData.justification.trim() || formData.justification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }
    
    try {
      await itemsAPI.editItem(selectedItem.id, {
        serial_number: formData.serial_number.trim(),
        imei1: formData.imei1.trim(),
        imei2: formData.imei2.trim(),
        imei3: formData.imei3.trim(),
        notes: formData.notes,
        justification: formData.justification.trim(),
      });
      toast.success('Identificadores actualizados com sucesso');
      setEditModalOpen(false);
      setSelectedItem(null);
      setFormData({ serial_number: '', imei1: '', imei2: '', imei3: '', notes: '', justification: '' });
      loadSerialNumbers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar identificadores'));
    }
  };

  const handleDelete = async () => {
    const reasonError = getReasonError(deleteReason);
    setDeleteReasonError(reasonError);
    if (reasonError) {
      toast.error(reasonError);
      return;
    }

    try {
      await serialNumbersAPI.remove(selectedItem.id, deleteReason.trim());
      toast.success('Número de série removido com sucesso');
      setDeleteModalOpen(false);
      setSelectedItem(null);
      setDeleteReason('');
      setDeleteReasonError('');
      loadSerialNumbers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao remover número de série'));
    }
  };

  const handleMove = async () => {
    if (!selectedItem) return;

    const reasonError = getReasonError(moveReason);
    setMoveReasonError(reasonError);
    if (reasonError) {
      toast.error(reasonError);
      return;
    }

    if (!moveLocationId) {
      toast.error('Selecione uma localização');
      return;
    }

    try {
      await serialNumbersAPI.move(selectedItem.id, moveLocationId, moveReason.trim());
      toast.success('Número de série movido com sucesso');
      setMoveModalOpen(false);
      setSelectedItem(null);
      setMoveReason('');
      setMoveReasonError('');
      setMoveLocationId('');
      loadSerialNumbers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao mover número de série'));
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormData({
      serial_number: item.serial_number || '',
      imei1: item.imei1 || '',
      imei2: item.imei2 || '',
      imei3: item.imei3 || '',
      notes: item.notes || '',
      justification: ''
    });
    setEditModalOpen(true);
  };

  const openDeleteModal = (item) => {
    setSelectedItem(item);
    setDeleteReason('');
    setDeleteReasonError('');
    setDeleteModalOpen(true);
  };

  const openMoveModal = (item) => {
    setSelectedItem(item);
    setMoveLocationId(item.location_id || '');
    setMoveReason('');
    setMoveReasonError('');
    setMoveModalOpen(true);
  };

  const getStateBadge = (state) => {
    switch (state) {
      case 'in_stock':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Em Stock</Badge>;
      case 'sold':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">Vendido</Badge>;
      case 'reserved':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Reservado</Badge>;
      case 'in_repair':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">Em Reparação</Badge>;
      default:
        return <Badge variant="outline">{state}</Badge>;
    }
  };

  return (
    <div className="space-y-4" data-testid="serial-numbers-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Hash className="h-5 w-5 text-blue-600" />
            {t('serialNumbersPanel.title')}
          </h3>
          <p className="text-sm text-muted-foreground" data-testid="serial-numbers-summary">
            {t('serialNumbersPanel.summary', { product: productName, count: total })}
          </p>
        </div>
        <Button
          data-testid="add-serial-number-button"
          onClick={() => { setFormData({ serial_number: '', notes: '' }); setAddModalOpen(true); }}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('serialNumbersPanel.addShort')}
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('serialNumbersPanel.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="serial-number-search-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={loadSerialNumbers}
          disabled={loading}
          data-testid="serial-number-refresh-button"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      </div>

      {/* Table */}
      <ScrollArea className="h-[400px] border rounded-lg">
        <Table data-testid="serial-numbers-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('serialNumbersPanel.identifier')}</TableHead>
              <TableHead>{t('serialNumbersPanel.stateLocation')}</TableHead>
              <TableHead>{t('serialNumbersPanel.details')}</TableHead>
              <TableHead>{t('labels.date')}</TableHead>
              <TableHead className="text-right">{t('salesPage.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8" data-testid="serial-numbers-loading">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : serialNumbers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-testid="serial-numbers-empty">
                  {t('serialNumbersPanel.empty')}
                </TableCell>
              </TableRow>
            ) : (
              serialNumbers.map((item) => (
                <TableRow key={item.id} data-testid={`sn-row-${item.id}`}>
                  <TableCell className="font-mono font-medium" data-testid={`sn-serial-${item.id}`}>
                    <div className="space-y-1">
                      <div>{getDisplayIdentifier(item)}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {item.is_deleted ? 'Registo inactivo' : 'Registo activo'}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell data-testid={`sn-state-${item.id}`}>
                    <div className="space-y-1">
                      {getStateBadge(item.state)}
                      <p className="text-xs text-muted-foreground" data-testid={`sn-location-${item.id}`}>
                        {item.location_name || item.location || '-'}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs space-y-1">
                    <p className="font-mono" data-testid={`sn-imei1-${item.id}`}>IMEI 1: {item.imei1 || '-'}</p>
                    <p className="font-mono" data-testid={`sn-imei2-${item.id}`}>IMEI 2: {item.imei2 || '-'}</p>
                    <p className="font-mono" data-testid={`sn-imei3-${item.id}`}>IMEI 3: {item.imei3 || '-'}</p>
                    <p className="font-mono" data-testid={`sn-serial-number-${item.id}`}>Série: {item.serial_number || '-'}</p>
                    <p className="text-muted-foreground truncate" data-testid={`sn-notes-${item.id}`}>{item.notes || 'Sem notas'}</p>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground" data-testid={`sn-created-${item.id}`}>
                    {item.created_at ? formatDateTime(item.created_at).split(' ')[0] : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditModal(item)}
                        disabled={!item.can_edit}
                        title={item.can_edit ? 'Editar' : 'Não é possível editar (vendido/reservado)'}
                        data-testid={`edit-sn-${item.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openMoveModal(item)}
                        disabled={item.can_move === false}
                        title={item.can_move === false ? 'Não é possível mover' : 'Mover'}
                        data-testid={`move-sn-${item.id}`}
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteModal(item)}
                        disabled={!item.can_delete}
                        title={item.can_delete ? 'Apagar' : 'Não é possível apagar (vendido)'}
                        data-testid={`delete-sn-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      {/* Add Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent data-testid="add-serial-number-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="add-serial-number-title">
              <Plus className="h-5 w-5 text-green-600" />
              Adicionar Número de Série
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label data-testid="add-serial-number-label">Número de Série *</Label>
              <Input
                placeholder="Ex: SN-12345678"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className="font-mono"
                data-testid="add-serial-number-input"
              />
            </div>
            <div>
              <Label data-testid="add-serial-number-notes-label">Notas</Label>
              <Textarea
                placeholder="Notas adicionais..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                data-testid="add-serial-number-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)} data-testid="add-serial-number-cancel-button">Cancelar</Button>
            <Button onClick={handleAdd} data-testid="add-serial-number-confirm-button">
              <CheckCircle className="h-4 w-4 mr-2" />
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent data-testid="edit-serial-number-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="edit-serial-number-title">
              <Edit className="h-5 w-5 text-blue-600" />
              Editar Número de Série
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label data-testid="edit-serial-number-label">Número de Série *</Label>
              <Input
                placeholder="Ex: SN-12345678"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className="font-mono"
                data-testid="edit-serial-number-input"
              />
            </div>
            <div>
              <Label data-testid="edit-imei1-label">IMEI 1</Label>
              <Input
                placeholder="Ex: 351234567890001"
                value={formData.imei1}
                onChange={(e) => setFormData({ ...formData, imei1: e.target.value })}
                className="font-mono"
                data-testid="edit-imei1-input"
              />
            </div>
            <div>
              <Label data-testid="edit-imei2-label">IMEI 2</Label>
              <Input
                placeholder="Ex: 351234567890002"
                value={formData.imei2}
                onChange={(e) => setFormData({ ...formData, imei2: e.target.value })}
                className="font-mono"
                data-testid="edit-imei2-input"
              />
            </div>
            <div>
              <Label data-testid="edit-imei3-label">IMEI 3</Label>
              <Input
                placeholder="Ex: 351234567890003"
                value={formData.imei3}
                onChange={(e) => setFormData({ ...formData, imei3: e.target.value })}
                className="font-mono"
                data-testid="edit-imei3-input"
              />
            </div>
            <div>
              <Label data-testid="edit-serial-number-notes-label">Notas</Label>
              <Textarea
                placeholder="Notas adicionais..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                data-testid="edit-serial-number-notes"
              />
            </div>
            <div>
              <Label data-testid="edit-item-justification-label">Justificação *</Label>
              <Textarea
                placeholder="Descreva o motivo da alteração..."
                value={formData.justification}
                onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
                rows={2}
                data-testid="edit-item-justification-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} data-testid="edit-serial-number-cancel-button">Cancelar</Button>
            <Button onClick={handleEdit} data-testid="edit-serial-number-confirm-button">
              <CheckCircle className="h-4 w-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Modal */}
      <Dialog open={moveModalOpen} onOpenChange={setMoveModalOpen}>
        <DialogContent data-testid="move-serial-number-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="move-serial-number-title">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              Mover Número de Série
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label data-testid="move-serial-number-location-label">Localização *</Label>
              <Select
                value={moveLocationId}
                onValueChange={setMoveLocationId}
                disabled={loadingLocations || locations.length === 0}
              >
                <SelectTrigger data-testid="move-serial-number-location-select">
                  <SelectValue
                    placeholder={loadingLocations ? 'A carregar localizações...' : 'Selecione uma localização'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem
                      key={location.id}
                      value={location.id}
                      data-testid={`move-serial-number-location-${location.id}`}
                    >
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!loadingLocations && locations.length === 0 && (
                <p className="text-xs text-amber-600 mt-2" data-testid="move-serial-number-no-locations">
                  Nenhuma localização disponível
                </p>
              )}
            </div>
            <div>
              <Label data-testid="move-serial-number-reason-label">Motivo *</Label>
              <Textarea
                placeholder="Motivo..."
                value={moveReason}
                onChange={(e) => {
                  setMoveReason(e.target.value);
                  if (moveReasonError) setMoveReasonError('');
                }}
                rows={2}
                data-testid="move-serial-number-reason-input"
              />
              {moveReasonError && (
                <p className="text-xs text-red-600" data-testid="move-serial-number-reason-error">
                  {moveReasonError}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMoveModalOpen(false)}
              data-testid="move-serial-number-cancel-button"
            >
              Cancelar
            </Button>
            <Button onClick={handleMove} data-testid="move-serial-number-confirm-button">
              Mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent data-testid="delete-serial-number-modal">
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2 text-red-600"
              data-testid="delete-serial-number-title"
            >
              <AlertTriangle className="h-5 w-5" />
              Apagar Número de Série
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground" data-testid="delete-serial-number-message">
              Tem a certeza que deseja remover o número de série:
            </p>
            <p className="font-mono font-semibold text-lg" data-testid="delete-serial-number-serial">
              {selectedItem?.display_sn}
            </p>
            <div>
              <Label data-testid="delete-serial-number-reason-label">Motivo *</Label>
              <Textarea
                placeholder="Motivo..."
                value={deleteReason}
                onChange={(e) => {
                  setDeleteReason(e.target.value);
                  if (deleteReasonError) setDeleteReasonError('');
                }}
                rows={2}
                data-testid="delete-serial-number-reason-input"
              />
              {deleteReasonError && (
                <p className="text-xs text-red-600" data-testid="delete-serial-number-reason-error">
                  {deleteReasonError}
                </p>
              )}
            </div>
            <p className="text-xs text-amber-600" data-testid="delete-serial-number-warning">
              Esta acção não pode ser revertida.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              data-testid="delete-serial-number-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="delete-serial-number-confirm-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Apagar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
